import { useState, useEffect, useCallback } from 'react';
import { Bell, CheckCheck, Trash2 } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { notificationService } from '../../services/api';
import { Button, Badge, Skeleton, EmptyState, Toast } from '../../components/ui';
import { thStyle, tdStyle, iconBtnStyle } from '../../utils/tableStyles';

const NotificationsPage = () => {
  const { dark } = useTheme();
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('all');
  const PAGE_SIZE = 20;

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await notificationService.getAll({
        page, limit: PAGE_SIZE,
        read: filter === 'unread' ? false : filter === 'read' ? true : undefined,
      });
      const items = data.data;
      setNotifications(Array.isArray(items) ? items : []);
      setTotal(data.total || 0);
    } catch { setToast({ msg: 'Error loading notifications', type: 'error' }); }
    finally { setLoading(false); }
  }, [page, filter]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleMarkRead = async (id) => {
    try {
      await notificationService.markAsRead(id);
      fetch();
    } catch { setToast({ msg: 'Error', type: 'error' }); }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setToast({ msg: 'All notifications marked as read', type: 'success' });
      fetch();
    } catch { setToast({ msg: 'Error', type: 'error' }); }
  };

  const handleDelete = async (id) => {
    try {
      await notificationService.markAsRead(id);
      fetch();
    } catch { setToast({ msg: 'Error', type: 'error' }); }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const unreadCount = notifications.filter(n => !n.isRead).length;

  const card = { background: dark ? '#0F172A' : '#fff', border: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}`, borderRadius: 16, overflow: 'hidden' };

  const TYPE_COLORS = { lead_assigned: '#3B82F6', lead_synced: '#10B981', new_lead: '#8B5CF6', system: '#6B7280', warning: '#F59E0B', error: '#EF4444' };

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: dark ? '#F1F5F9' : '#0F172A', margin: 0 }}>Notifications</h1>
          {unreadCount > 0 && <p style={{ fontSize: 13, color: '#3B82F6', margin: '4px 0 0' }}>{unreadCount} unread notification{unreadCount > 1 ? 's' : ''}</p>}
        </div>
        <Button variant="outline" icon={CheckCheck} onClick={handleMarkAllRead}>Mark All Read</Button>
      </div>

      <div style={card}>
        <div style={{ display: 'flex', gap: 6, padding: '12px 20px', borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
          {['all', 'unread', 'read'].map(f => (
            <button key={f} onClick={() => { setFilter(f); setPage(1); }} style={{
              padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', border: 'none',
              background: filter === f ? '#3B82F6' : (dark ? '#1E293B' : '#F1F5F9'),
              color: filter === f ? '#fff' : (dark ? '#94A3B8' : '#64748B'),
              textTransform: 'capitalize',
            }}>{f}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 20 }}><Skeleton rows={6} cols={3} /></div>
        ) : notifications.length === 0 ? (
          <div style={{ padding: 40 }}><EmptyState icon={Bell} title="No notifications" description="You're all caught up!" /></div>
        ) : (
          <div>
            {notifications.map(n => (
              <div key={n._id} style={{
                display: 'flex', gap: 12, padding: '14px 20px',
                borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}`,
                background: !n.isRead ? (dark ? '#0F1F3D' : '#F0F7FF') : 'transparent',
                transition: 'background 0.2s',
              }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: `${TYPE_COLORS[n.type] || '#6B7280'}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Bell size={16} color={TYPE_COLORS[n.type] || '#6B7280'} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: n.isRead ? 400 : 600, color: dark ? '#F1F5F9' : '#0F172A', marginBottom: 2 }}>{n.title}</div>
                  <div style={{ fontSize: 12, color: dark ? '#94A3B8' : '#64748B', lineHeight: 1.4 }}>{n.message}</div>
                  <div style={{ fontSize: 11, color: dark ? '#475569' : '#94A3B8', marginTop: 4 }}>{new Date(n.createdAt).toLocaleString()}</div>
                </div>
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  {!n.isRead && <button title="Mark as read" style={iconBtnStyle(dark)} onClick={() => handleMarkRead(n._id)}><CheckCheck size={14} /></button>}
                  <button title="Delete" style={{ ...iconBtnStyle(dark), color: '#EF4444' }} onClick={() => handleDelete(n._id)}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderTop: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
            <span style={{ fontSize: 12, color: dark ? '#64748B' : '#94A3B8' }}>Page {page} of {totalPages}</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
